/**
 * RAG server URL normalization.
 *
 * The intended architecture (per docker/nginx.conf) is for the frontend to
 * reach the RAG via the demo's nginx at a same-origin `/rag-proxy/<instance>`
 * path. Going same-origin avoids CORS preflights and keeps the RAG server
 * private to the Container Apps environment.
 *
 * In practice, persona records sometimes get a full absolute URL written
 * into `rag_config.server_url` (manual edit, orchestrator misconfig, copied
 * from the backend's RAG_SERVER_URL env var, etc.). When the browser then
 * fetches that absolute URL it goes cross-origin, fails CORS, and breaks
 * the chat surface for that persona.
 *
 * `normalizeRagUrl` defends against this misconfiguration: when the URL
 * is absolute and points at the *same* Azure Container Apps environment
 * the app is currently running in, it is rewritten to the same-origin
 * `/rag-proxy/<instance>...` form. URLs to other origins (custom self-
 * hosted RAGs, different Container Apps envs, localhost dev, etc.) are
 * left alone.
 */

export interface NormalizeOptions {
  /**
   * The Container Apps env domain of the running app, e.g.
   * "livelywater-334394bc.westus2.azurecontainerapps.io". When absent,
   * derived from `window.location.hostname`. Pass an explicit value in
   * tests or non-browser contexts. Pass `null` to disable rewrite.
   */
  envDomain?: string | null
}

export function normalizeRagUrl(url: string, opts: NormalizeOptions = {}): string {
  if (!url) return url

  const stripped = url.replace(/\/+$/, '')

  // Already-relative path — just ensure leading slash.
  if (stripped.startsWith('/')) return stripped

  // Plain "rag-proxy/foo" without scheme — treat as relative.
  if (!/^https?:\/\//i.test(stripped)) return '/' + stripped

  // Absolute URL — rewrite only if it points at the same Container Apps env.
  const envDomain =
    opts.envDomain === undefined ? currentEnvDomain() : opts.envDomain
  if (!envDomain) return stripped

  let target: URL
  try {
    target = new URL(stripped)
  } catch {
    return stripped
  }

  const suffix = '.' + envDomain
  if (!target.hostname.endsWith(suffix)) return stripped

  const instance = target.hostname.slice(0, -suffix.length)
  if (!instance) return stripped // malformed: hostname == ".env"

  const path = target.pathname.replace(/\/+$/, '')
  return `/rag-proxy/${instance}${path}`
}

/**
 * Derive the Container Apps env domain from the current page's hostname.
 * For `ai-siemens-rag-demo.livelywater-334394bc.westus2.azurecontainerapps.io`,
 * returns `livelywater-334394bc.westus2.azurecontainerapps.io`.
 *
 * Returns `undefined` when not running in Container Apps (localhost,
 * custom domain, SSR/no `window`, etc.) — caller should leave URLs alone
 * in that case.
 */
function currentEnvDomain(): string | undefined {
  if (typeof window === 'undefined' || !window.location) return undefined
  const host = window.location.hostname
  if (!host.endsWith('.azurecontainerapps.io')) return undefined
  const dotIdx = host.indexOf('.')
  return dotIdx > 0 ? host.slice(dotIdx + 1) : undefined
}
